<template>
  <div ref="scrollContainer" class="document-editor" @click="onRootClick" @scroll="handleScroll">
    <BbLoadingIndicator v-if="canvasLoading" class="document-editor__loader" />

    <div class="actions-bar-overlay" :style="{ width: actionBarComputedWidth.actionWrapper }">
      <div class="actions-bar-overlay__border" :style="{ width: actionBarComputedWidth.actionBar }">-</div>
    </div>
    <DocumentActionsBar
      @onUndo="undo"
      @onRedo="redo"
      :width="actionBarComputedWidth.actionBar"
      :class="{ scrolling: isScrolling || scrollPosition > 0 }"
    />

    <EditorCanvas
      ref="canvasRef"
      :content="editorContent"
      :settings="editorSettings"
      :viewport="editorViewport"
      :labels="editorLabels"
      :debug="false"
      :class="{ 'overflow-visible': isFirstLayoutSelected }"
      @wesley:message="onWesleyMessage"
      @loading="onCanvasLoading(true)"
      @load="onCanvasLoading(false)"
    >
      <template v-slot:ui>
        <ElementHighlight
          v-if="selectedLayout"
          :element="selectedLayout"
          :class="{ 'has-child-selected': selectedElement }"
        >
          <template v-slot:label>
            <AIAutopopulateSwitch />
          </template>

          <template v-if="displayLayoutActions">
            <BbTooltip
              v-for="placement in ['before', 'after']"
              :key="placement"
              v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('author.addLayoutTooltip') }"
            >
              <InsertLayoutButton
                :class="`document-editor__insert-${placement}-button`"
                @click="setPositionToInsert(placement)"
              />
            </BbTooltip>
          </template>

          <template v-if="displayFootnotesSwitcher" v-slot:bottom-label>
            <ShowFootnotesSwitch />
          </template>

          <DocumentEditorActions
            v-if="displayLayoutActions"
            :displayVisibilitySettings="true"
            @remove="onRemoveLayout"
            @move-up="onMoveLayoutUp"
            @move-down="onMoveLayoutDown"
          />
        </ElementHighlight>

        <ElementHighlight v-if="selectedElement" :element="selectedElement">
          <div class="image-buttons-container">
            <ReplaceImageLabel
              v-if="shouldDisplayReplaceLabel"
              @click="setMediaPanelOpened({ cssPath: selectedElement.cssPath, opened: true })"
            />
            <EditImageLabel v-if="shouldDisplayEditLabel" @click="editImage(selectedElement)" />
          </div>
        </ElementHighlight>

        <ElementHighlight v-if="hoverElement" :element="hoverElement" is-hover />

        <EditorTools :tools="tools" @custom-tool="onCustomTool" />
      </template>
    </EditorCanvas>
  </div>

  <LayoutsListPopup @layout-selected="onInsertLayoutClick" />
  <ReferencesListPopup />
  <MediaPanel v-if="mediaPanelOpened.opened" />
  <DocumentPreviewDialog v-if="previewAuthorDocument" :editorContent="previewContent" />
  <BbPopup v-model="showCropperModal" :title="t('author.editImage.editImage')" width="800px">
    <ImageCropper
      v-if="showCropperModal && currentImageForEdit.originalUrl"
      :image-src="currentImageForEdit.originalUrl"
      :css-path="currentImageForEdit.cssPath"
      @cancel="closeCropperModal"
    />
  </BbPopup>
</template>

<script>
import { computed, ref, watch, onBeforeUpdate, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from 'lodash';

import {
  EditorCanvas,
  EditorTools,
  ElementHighlight,
  useElementHighlight,
  useWesley,
} from '@anthill-modules/wesley-components';
import '@anthill-modules/wesley-components/style.css';
import { useRoute } from 'vue-router/dist/vue-router';

import { fontTools, selectors } from '~/constants/documentEditor';

import useDocumentEditor from '~/composables/useDocumentEditor';
import useAuthoring from '~/composables/useAuthoring';
import useModals from '~/composables/useModals';
import createHtmlHistory from '~/composables/createHtmlHistory';
import useDocuments from '~/composables/useDocuments';

import {
  isImage,
  extractImageExternalId,
  isImageUrl,
  stripOldWesleyImports,
  extractUserImageFolderName,
  isCroppedImageUrl,
  extractCroppedImageUrl,
  extractOriginalImageUrl,
} from '~/utils/editor-utils';
import { transformDocReferences } from '~/utils/citations-utils';

import DocumentActionsBar from '~/components/conversation/authoring/DocumentActionsBar';
import InsertLayoutButton from '~/components/common/InsertLayoutButton';
import AIAutopopulateSwitch from '~/components/common/AIAutopopulateSwitch';
import ShowFootnotesSwitch from '~/components/common/ShowFootnotesSwitch';
import LayoutsListPopup from '~/components/common/LayoutsListPopup.vue';
import DocumentEditorActions from '~/components/conversation/authoring/DocumentEditorActions.vue';
import MediaPanel from '~/components/common/mediaPanel/MediaPanel.vue';
import DocumentPreviewDialog from '~/components/conversation/authoring/documentPreview/DocumentPreviewDialog.vue';
import ReplaceImageLabel from '~/components/common/ReplaceImageLabel.vue';
import EditImageLabel from '~/components/common/EditImageLabel.vue';
import ReferencesListPopup from '~/components/common/references/ReferencesListPopup.vue';
import ImageCropper from '~/components/common/imageEditing/ImageCropper.vue';

export default {
  components: {
    ReferencesListPopup,
    EditorCanvas,
    EditorTools,
    ElementHighlight,
    DocumentPreviewDialog,
    LayoutsListPopup,
    InsertLayoutButton,
    DocumentEditorActions,
    AIAutopopulateSwitch,
    DocumentActionsBar,
    MediaPanel,
    ReplaceImageLabel,
    ShowFootnotesSwitch,
    EditImageLabel,
    ImageCropper,
  },
  props: {
    html: { type: String, default: '' },
    forceRerender: { type: Number, default: 0 },
  },
  emits: ['saveContent', 'handleKeydown', 'onUndo', 'onRedo'],

  setup(props, { emit }) {
    const { t, messages, locale } = useI18n();
    const route = useRoute();

    const {
      currentDsConfig,
      emailTokens,
      setSelectedObjectDocumentEditor,
      handleDocumentResponseEvent,
      getLayoutById,
      renderCitationTooltipsInIframe,
      updateEditorSelectableElementsData,
      selectedObjectDocumentEditor,
      saveHtmlDocumentContentToDb,
      refreshReferencesOnUpdate,
    } = useDocumentEditor();
    const { footnotes, displayReferencesOnDocumentLevel, setDisplayReferencesOnDocumentLevel } = useAuthoring();
    const { currentHtml, saveEditorContentStateFromIframe } = createHtmlHistory();
    const {
      mediaPanelOpened,
      setMediaPanelOpened,
      previewAuthorDocument,
      setReferencesListOpened,
      setLayoutsListOpened,
    } = useModals();
    const { getAuthoredDocumentThumbs, getAuthoredUserImages } = useDocuments();
    const {
      iframeWindow,
      sendMessage: sendMessageToDocument,
      asyncSendMessage: sendAsyncMessageToDocument,
    } = useWesley();
    const { selectedParent: selectedLayout, selectedElement, hoverElement } = useElementHighlight();

    const editorLabels = computed(() => messages.value[locale.value].author.editor);

    const canvasRef = ref();
    const canvasLoading = ref(true);
    const positionToInsertLayout = ref('');
    const scrollContainer = ref(null);
    const scrollPosition = ref(0);
    const hasSavedScroll = ref(false); // flag to prevent duplicate saves
    const isScrolling = ref(false);
    const editorViewport = ref({ scale: 100 });
    const showCropperModal = ref(false);
    const currentImageForEdit = ref(null);
    let scrollTimeout;

    const displayLayoutActions = computed(() => selectedLayout.value && !selectedElement.value);
    const displayFootnotesSwitcher = computed(
      () => displayLayoutActions.value && footnotes.value.length && !displayReferencesOnDocumentLevel.value,
    );
    const isFirstLayoutSelected = computed(() => selectedLayout.value && selectedLayout.value?.boundingBox?.top === 0);

    const handleScroll = () => {
      isScrolling.value = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isScrolling.value = false;
      }, 200);
    };

    const tools = computed(() => {
      const ts = [...fontTools];
      ts.push('', { key: 'token', options: emailTokens.value, groupKey: 'category' });

      ts.push('', 'link');
      ts.push('', { key: 'color', colors: colorOptions.value });
      ts.push('', 'whiteSpace');

      ts.push('', 'clear');
      ts.push('', 'citation');

      return ts;
    });

    const colorOptions = computed(() =>
      Object.entries(currentDsConfig?.value.colors || {}).map(([label, value]) => ({
        value,
        label,
      })),
    );

    const debouncerTimer = ref(null);

    const editorContent = ref(stripOldWesleyImports(props.html));
    const previewContent = computed(() =>
      transformDocReferences(stripOldWesleyImports(currentHtml.value || props.html), footnotes.value),
    );
    const contentType = computed(() => {
      if (editorContent.value.includes('<mjml')) {
        return 'mjml';
      }
      return 'html';
    });

    const editorSettings = computed(() => {
      return {
        root: selectors[contentType.value].root,
        selectable: selectors[contentType.value].layout,
        childrenSelectable: '[show-in-editor="true"]',
        editable: true,
        'tiptap.enabled': true,
        richTextElements: selectors[contentType.value].richText,
      };
    });

    const shouldDisplayReplaceLabel = computed(() => selectedElement.value && isImage(selectedElement.value));
    const shouldDisplayEditLabel = computed(
      () => shouldDisplayReplaceLabel.value && isImageUrl(selectedElement.value.attributes.src),
    );

    const actionBarComputedWidth = computed(() => {
      const viewport = !canvasLoading.value && canvasRef.value?.$el.querySelector('.editor-canvas__viewport');
      const width = !canvasLoading.value ? viewport?.offsetWidth : 600;
      return {
        actionBar: `${width + 3}px`,
        actionWrapper: `${width + 8}px`,
      };
    });

    const setPositionToInsert = (position) => {
      positionToInsertLayout.value = position;
      setLayoutsListOpened(true);
    };

    const onInsertLayoutClick = async (layout) => {
      const layoutToBeInserted = await getLayoutById(layout.id);
      switch (positionToInsertLayout.value) {
        case 'before':
          sendMessageToDocument({
            method: 'insertBefore',
            params: {
              query: selectedLayout.value.cssPath,
              element: layoutToBeInserted.content,
            },
          });
          break;
        case 'after':
          sendMessageToDocument({
            method: 'insertAfter',
            params: {
              query: selectedLayout.value.cssPath,
              element: layoutToBeInserted.content,
            },
          });
          break;
        default:
          return;
      }
      emit('saveContent');
      updateEditorSelectableElementsData();
    };

    const undo = () => {
      emit('onUndo');
    };

    const redo = () => {
      emit('onRedo');
    };

    const onCustomTool = ({ tool }) => {
      if (tool.key === 'citation') {
        onOpenReferencesList();
      }
    };

    const onOpenReferencesList = async () => {
      setReferencesListOpened({ open: true, editable: false });
    };

    const onSaveEditorContent = () => {
      clearTimeout(debouncerTimer.value); // Clear previous timer if there is one
      debouncerTimer.value = setTimeout(() => {
        emit('saveContent');
      });
    };

    const onWesleyMessage = ({ event, data }) => {
      switch (event) {
        case 'we:error':
          console.error('Web Editor Error:', data.error);
          break;

        case 'we:loaded':
          sendMessageToDocument({
            method: 'batch',
            instructions: [
              {
                method: 'extendAttributesList',
                params: {
                  '*': ['data-ant-field'],
                  'mj-wrapper': ['data-ant-locked', 'show-footnotes'],
                },
              },
              {
                method: 'getSelectableElementsData',
              },
            ],
          });
          // Wait a short moment and then populate image srcs from cache/backend
          setTimeout(() => {
            loadDocumentImages();
          }, 300);
          break;

        case 'we:deselected:element':
          onSaveEditorContent();
          if (data.hasChanged) {
            console.log('Changes were made:', data.changeSet, data.text);
          }

          break;

        case 'we:response':
          handleDocumentResponseEvent(data);
          break;

        case 'we:editor:typing':
          // when user is typing in document, this event is sent
          // it is needed to compute the overlay bullets in realTime
          debouncedRenderCitationTooltips();
          break;

        case 'we:editor:destroyed':
          // when user is typing in document, this event is sent
          // it is needed to compute the overlay bullets in realTime
          debouncedRenderCitationTooltips();
          saveHtmlDocumentContentToDb();
          break;

        default:
          // console.log('Unhandled Web Editor event:', data);
          break;
      }
    };

    const onRootClick = (event) => {
      if (!event.target.classList.contains('document-editor')) return;

      sendMessageToDocument({ method: 'deselectElement' });
    };

    // set the body attribute show-footnotes-on-document (by default true if value doesn't exist)
    const setInitialFootnotesPosition = () => {
      const iframeBody = iframeWindow.value?.document?.body;
      if (iframeBody?.attributes && !iframeBody?.attributes?.['show-footnotes-on-document']?.value) {
        // for footnotes to be displayed on document level
        iframeBody?.setAttribute('show-footnotes-on-document', 'true');
      }
      // if value already exists, keep it
      setDisplayReferencesOnDocumentLevel(!!iframeBody?.attributes?.['show-footnotes-on-document']?.value);
    };

    const onCanvasLoading = async (isLoading = true) => {
      canvasLoading.value = isLoading;

      if (!isLoading) {
        await loadDocumentImages();
        renderCitationTooltipsInIframe();
        setInitialFootnotesPosition();

        if (scrollContainer.value && hasSavedScroll.value) {
          scrollContainer.value.scrollTop = scrollPosition.value;
          hasSavedScroll.value = false;
        }
      }
    };

    const loadDocumentImages = async () => {
      const iframeDoc = iframeWindow.value?.document;
      if (!iframeDoc) return;

      const images = [...iframeDoc.querySelectorAll('img, mj-image')].filter((img) => isImageUrl(img.src));

      if (images.length) {
        // Separate document images and user images
        const documentImageIds = [];
        const userImageIds = [];

        images.forEach((image) => {
          const externalId = extractImageExternalId(image.src);
          if (externalId) {
            documentImageIds.push(externalId);
          } else {
            const userImageId = extractUserImageFolderName(image.src);
            if (userImageId) {
              userImageIds.push(userImageId);
            }
          }
        });

        // Get unique IDs
        const uniqueDocumentIds = [...new Set(documentImageIds)];
        const uniqueUserImageIds = [...new Set(userImageIds)];

        // Fetch all image variants from backend (includes original + cropped)
        const [documentImagesFromBE, userImagesFromBE] = await Promise.all([
          uniqueDocumentIds.length
            ? getAuthoredDocumentThumbs(uniqueDocumentIds, false, true, route.params.conversationId)
            : [],
          uniqueUserImageIds.length ? getAuthoredUserImages(route.params.conversationId, uniqueUserImageIds, true) : [],
        ]);

        const instructions = [];

        // Process each image - check if it's document/user image and if it's cropped or original
        images.forEach((image) => {
          const documentExtId = extractImageExternalId(image.src);
          const userImageId = extractUserImageFolderName(image.src);
          let imageUrl = null;

          if (documentExtId) {
            // It's a document image
            if (isCroppedImageUrl(image.src)) {
              // It's cropped image
              imageUrl = extractCroppedImageUrl(image.src, documentImagesFromBE);
            } else {
              // It's an original image
              imageUrl = extractOriginalImageUrl(documentExtId, documentImagesFromBE);
            }
          } else if (userImageId) {
            // It's a user image
            if (isCroppedImageUrl(image.src)) {
              // It's cropped image
              imageUrl = extractCroppedImageUrl(image.src, userImagesFromBE);
            } else {
              // It's an original image
              imageUrl = extractOriginalImageUrl(userImageId, userImagesFromBE);
            }
          }

          if (imageUrl) {
            instructions.push({
              method: 'updateAttribute',
              params: {
                query: `#${image.id}`,
                value: imageUrl,
                attr: 'src',
              },
            });
          }
        });

        if (instructions.length) {
          // send updates and wait for Wesley to apply them (safe wrapper prevents UI-level throws)
          try {
            await sendAsyncMessageToDocument({ method: 'batch', instructions });
          } catch (e) {
            console.error(e);
          }

          const targetUrls = instructions.map((ins) => ins.params && ins.params.value).filter(Boolean);
          const urlsToWaitFor = (targetUrls.length ? targetUrls : images.map((img) => img.src)).filter(Boolean);
          const uniqueUrls = [...new Set(urlsToWaitFor)];

          const loadPromises = uniqueUrls.map(
            (src) =>
              new Promise((resolve) => {
                const img = new window.Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                setTimeout(() => resolve(false), 3000);
                img.src = src;
              }),
          );

          await Promise.all(loadPromises);
        }
      }
    };

    const debouncedRenderCitationTooltips = debounce(renderCitationTooltipsInIframe, 100);

    // Capture scroll BEFORE DOM updates
    onBeforeUpdate(() => {
      if (scrollContainer.value && !hasSavedScroll.value) {
        scrollPosition.value = scrollContainer.value.scrollTop;
      }
    });

    const refreshReferences = () => {
      const iframeBody = iframeWindow.value?.document?.body;

      if (iframeBody?.innerHTML) {
        setTimeout(() => {
          refreshReferencesOnUpdate();
        }, 2000);
      }
    };

    watch(
      [selectedLayout, selectedElement],
      ([newLayout, newElement]) => {
        setSelectedObjectDocumentEditor({ selectedLayout: newLayout, selectedElement: newElement });
      },
      { immediate: true, deep: true },
    );

    watch(
      () => props.forceRerender,
      (newValue, oldValue) => {
        if (newValue !== oldValue) {
          hasSavedScroll.value = true;
          editorContent.value = stripOldWesleyImports(props.html);
          if (scrollContainer.value) {
            scrollContainer.value.scrollTop = scrollPosition.value;
          }
        }
      },
    );

    onUnmounted(() => {
      sessionStorage.clear();
    });

    watch(
      () => footnotes.value,
      (newFootnotes) => {
        refreshReferences(newFootnotes);
      },
      { immediate: true, deep: true },
    );

    const editImage = (element) => {
      currentImageForEdit.value = {
        cssPath: element.cssPath,
        originalUrl: element.attributes.src,
      };
      showCropperModal.value = true;
    };

    const closeCropperModal = () => {
      showCropperModal.value = false;
      currentImageForEdit.value = null;
    };

    const onRemoveLayout = () => {
      if (!selectedObjectDocumentEditor.value?.selectedLayout?.cssPath) return;
      sendMessageToDocument({
        method: 'removeElement',
        params: {
          query: selectedObjectDocumentEditor.value.selectedLayout.cssPath,
        },
      });
      updateEditorSelectableElementsData();
      saveEditorContentStateFromIframe();

      setTimeout(async () => {
        await saveHtmlDocumentContentToDb();
      }, 500);
    };

    const onMoveLayoutDown = () => {
      sendMessageToDocument({
        method: 'moveDown',
      });
      updateEditorSelectableElementsData();
      saveEditorContentStateFromIframe();

      setTimeout(async () => {
        await saveHtmlDocumentContentToDb();
      }, 500);
    };
    const onMoveLayoutUp = () => {
      sendMessageToDocument({
        method: 'moveUp',
      });
      updateEditorSelectableElementsData();
      saveEditorContentStateFromIframe();

      setTimeout(async () => {
        await saveHtmlDocumentContentToDb();
      }, 500);
    };

    return {
      editorLabels,
      canvasRef,
      canvasLoading,
      editorSettings,
      editorContent,
      editorViewport,
      previewContent,
      onCanvasLoading,
      onWesleyMessage,

      selectedLayout,
      selectedElement,
      hoverElement,
      tools,
      onRootClick,
      t,
      scrollContainer,
      displayLayoutActions,
      onInsertLayoutClick,
      setPositionToInsert,
      mediaPanelOpened,
      undo,
      redo,
      previewAuthorDocument,
      shouldDisplayReplaceLabel,
      shouldDisplayEditLabel,
      setMediaPanelOpened,
      isScrolling,
      handleScroll,
      scrollPosition,
      actionBarComputedWidth,
      onRemoveLayout,
      onMoveLayoutUp,
      onMoveLayoutDown,
      onCustomTool,
      onOpenReferencesList,
      displayFootnotesSwitcher,
      isFirstLayoutSelected,
      showCropperModal,
      currentImageForEdit,
      editImage,
      closeCropperModal,
    };
  },
};
</script>

<style lang="scss" scoped>
.actions-bar {
  &.scrolling {
    z-index: 2;
  }
}

.actions-bar-overlay {
  position: sticky;
  top: 0;
  height: 4px; // Match the height of the actions-bar
  margin: auto;
  background: $bb-neutral-0;
  z-index: 1;
  flex-shrink: 0;

  &__border {
    color: transparent;
    height: 8px;
    margin-top: 2px !important;
  }
}

.document-editor {
  display: flex;
  height: 100%;
  width: 100%;
  flex: 1;
  flex-direction: column;
  position: relative;
  overflow: auto;
  transition: none;
  margin: 0 $bb-spacing-4 * -1 $bb-spacing-4 * -1;

  &::-webkit-scrollbar {
    display: none;
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: $bb-neutral-0;
    z-index: 1000;

    :deep(.bounce) {
      background-color: $bb-primary-1 !important;
    }
  }

  :deep(.editor-canvas) {
    margin: 0 auto;
    position: relative;
    padding: 0;
    width: 100%;
    scrollbar-width: none;
    -ms-overflow-style: none;

    .overflow-visible {
      overflow: visible;
    }
  }

  :deep(.editor-canvas__viewport) {
    border-radius: 0 0 8px 8px;
    border: 1px solid $bb-neutral-4;
    border-top: 0;
    box-shadow: 0px 5px 8px 0px rgba(0, 0, 0, 0.1);
  }

  :deep(.element-highlight) {
    outline: 2px solid $bb-interface-7;

    .element-highlight__label {
      left: -2px;
      background: $bb-interface-7;
    }

    .element-label__show-footnotes-wrapper {
      bottom: 0 !important;
    }

    .image-buttons-container {
      display: flex;
      gap: 8px;
      justify-content: center;
      align-items: center;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      z-index: 1000;
    }
  }

  :deep(.element-highlight).is-parent:not(.is-hover) {
    outline: 2px solid $bb-primary-3;
    z-index: 10;

    .bb-btn.primary:hover {
      background: $bb-primary-3 !important;
    }

    .element-highlight__label {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      height: fit-content;
      font-size: 12px;
      top: auto;
      left: -2px;
      bottom: calc(100% + 2px);
      padding: $bb-spacing-1 $bb-spacing-2;
      background: rgba($bb-primary-1, 0.85);
      border-radius: $bb-spacing-1;
      pointer-events: auto;

      .bb-icon {
        display: none;
      }
    }

    &.has-child-selected .element-highlight__label {
      display: none;
    }

    .element-highlight__name {
      margin-left: 0;
    }
  }

  :deep(.editor-tools) {
    z-index: 20;
    display: flex;
    max-width: 620px;
    flex-direction: row;
    flex-wrap: wrap;
    width: 617px;
    left: 306px !important;

    .editor-tools__btn,
    .editor-tools__item .editor-tools__btn.bb-btn.icon {
      &:active,
      &.v-btn--active,
      &:hover {
        background-color: $bb-interface-8 !important;
        color: $bb-neutral-1 !important;
      }
    }
  }

  :deep(.v-overlay__content.bb-menu.options-menu__options.small) {
    min-width: 300px !important;
  }

  &__insert-after-button,
  &__insert-before-button {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;

    &:hover {
      background: $bb-primary-3 !important;
    }
  }

  &__insert-after-button {
    bottom: -17px;
  }

  &__insert-before-button {
    top: -20px;
  }
}
</style>
